import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { customersAPI } from '../services/api';
import { Search, Filter, Calendar } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

const CustomerSubscriptions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [sortBy, setSortBy] = useState('created_at');

  const { customers: customerPerms } = usePermissions();

  const { data: subsData, isLoading, error } = useQuery(
    ['customerSubscriptions', currentPage, pageSize, search, subscriptionStatus, sortBy],
    () => customersAPI.getWithSubscriptions({ 
      page: currentPage, 
      limit: pageSize,
      search,
      subscription_status: subscriptionStatus,
      sort_by: sortBy
    }),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: (error) => {
        console.error('Customer subscriptions fetch error:', error);
        toast.error('Failed to load customer subscriptions');
      }
    }
  );

  let subscriptions = [];
  let pagination = { page: 1, limit: 10, total: 0, pages: 1 };

  if (Array.isArray(subsData?.data?.data)) {
    subscriptions = subsData.data.data;
    pagination = subsData.data.pagination || pagination;
  } else if (Array.isArray(subsData?.data)) {
    subscriptions = subsData.data;
    pagination = subsData.data.pagination || pagination;
  }

  const groupedByCustomer = subscriptions.reduce((acc, sub) => {
    const key = sub.uuid;
    if (!acc[key]) {
      acc[key] = {
        uuid: sub.uuid,
        name: sub.name,
        email: sub.email,
        phone: sub.phone,
        created_at: sub.created_at,
        subscriptions: []
      };
    }
    if (sub.subscription_uuid) {
      acc[key].subscriptions.push({
        uuid: sub.subscription_uuid,
        plan_name: sub.plan_name,
        product_name: sub.product_name,
        quantity: sub.quantity,
        status: sub.subscription_status,
        schedule_type: sub.schedule_type,
        delivery_slot: sub.delivery_slot,
        next_delivery_date: sub.next_delivery_date,
        start_date: sub.start_date,
        end_date: sub.end_date,
        total_deliveries: sub.total_deliveries,
        remaining_deliveries: sub.remaining_deliveries,
        total_amount: sub.total_amount
      });
    }
    return acc;
  }, {});

  const customers = Object.values(groupedByCustomer);

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400';
      case 'paused':
        return 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400';
      case 'cancelled':
        return 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400';
      case 'expired':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getScheduleLabel = (scheduleType) => {
    const labels = {
      daily: '📅 Daily',
      alternative: '📅 Alternative Days',
      custom: '📅 Custom'
    };
    return labels[scheduleType] || scheduleType;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load customer subscriptions</div>
          <div className="text-gray-500 dark:text-gray-400 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="text-left">
          <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Customer Subscriptions</h1>
          <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
            View all customers with active subscriptions and their subscription details
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card-modern p-4 sm:p-6 rounded-xl space-y-4 animate-fade-in-up">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by customer name, email, or plan..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Subscription Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={subscriptionStatus}
              onChange={(e) => {
                setSubscriptionStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {/* Sort */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="created_at">Newest First</option>
              <option value="name">Customer Name</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customers List */}
      <div className="space-y-4">
        {customers.length > 0 ? (
          customers.map((customer, idx) => (
            <div key={customer.uuid} className="card-modern rounded-xl overflow-hidden animate-fade-in-up" style={{ animationDelay: `${idx * 0.05}s` }}>
              {/* Customer Header */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-600">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">{customer.name}</h3>
                    <div className="flex flex-col sm:flex-row gap-3 mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {customer.email && <span>📧 {customer.email}</span>}
                      {customer.phone && <span>📱 {customer.phone}</span>}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Joined: {formatDate(customer.created_at)}
                  </div>
                </div>
              </div>

              {/* Subscriptions */}
              <div className="p-4 sm:p-6 space-y-4">
                {customer.subscriptions.length > 0 ? (
                  customer.subscriptions.map((sub) => (
                    <div key={sub.uuid} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Plan Name */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Plan Name</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{sub.plan_name || '-'}</p>
                        </div>

                        {/* Product */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Product</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{sub.product_name || '-'}</p>
                        </div>

                        {/* Quantity */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Quantity</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{sub.quantity || 0}</p>
                        </div>

                        {/* Status */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-1 ${getStatusBadgeColor(sub.status)}`}>
                            {sub.status?.charAt(0).toUpperCase() + sub.status?.slice(1) || '-'}
                          </span>
                        </div>

                        {/* Schedule Type */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Schedule</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{getScheduleLabel(sub.schedule_type)}</p>
                        </div>

                        {/* Delivery Slot */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Slot</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 capitalize">{sub.delivery_slot || 'Morning'}</p>
                        </div>

                        {/* Start Date */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Start Date</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{formatDate(sub.start_date)}</p>
                        </div>

                        {/* End Date */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">End Date</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{formatDate(sub.end_date)}</p>
                        </div>

                        {/* Total Deliveries */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Total Deliveries</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{sub.total_deliveries || 0}</p>
                        </div>

                        {/* Remaining Deliveries */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Remaining</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{sub.remaining_deliveries || 0}</p>
                        </div>

                        {/* Total Amount */}
                        <div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Total Amount</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{formatCurrency(sub.total_amount)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 dark:text-gray-400">No subscriptions for this customer</p>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="card-modern p-12 text-center rounded-xl">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No customer subscriptions found</h3>
            <p className="text-gray-600 dark:text-gray-300">No customers have active subscriptions yet</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="card-modern p-4 rounded-xl flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} customers
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Previous
            </button>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 rounded-lg ${currentPage === page ? 'bg-indigo-600 text-white' : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'} transition`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
              disabled={currentPage === pagination.pages}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSubscriptions;
